import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Calculator, ArrowRight, Video, BarChart, Zap, ChevronRight } from 'lucide-react';
import axios from 'axios';

const Pricing = () => {
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState(50);
    const [unitsPerStudent, setUnitsPerStudent] = useState(5);

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await axios.get('/subscriptions/packages');
            setPackages(response.data);
        } catch (error) {
            console.error('Error fetching packages:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateMonthlyCost = (pricePerStudentPerUnit) => {
        return students * unitsPerStudent * parseFloat(pricePerStudentPerUnit);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const basicPackage = packages.find(p => p.name === 'basic');
    const proPackage = packages.find(p => p.name === 'pro');

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Pricing Plans
                    </h1>
                    <p className="text-xl text-white/90 mb-8">
                        Choose the perfect plan for your institution with 30-day free trial
                    </p>
                </div>
            </div>

            {/* Pricing Calculator */}
            <div className="max-w-4xl mx-auto px-6 -mt-8">
                <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
                    <div className="flex items-center justify-center mb-6">
                        <Calculator className="w-6 h-6 text-blue-600 mr-2" />
                        <h2 className="text-2xl font-bold text-gray-900">Cost Calculator</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Number of Students
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={students}
                                onChange={(e) => setStudents(parseInt(e.target.value) || 1)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Units per Student (Monthly)
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={unitsPerStudent}
                                onChange={(e) => setUnitsPerStudent(parseInt(e.target.value) || 1)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {basicPackage && (
                                <div className="text-center">
                                    <p className="text-sm text-gray-600 mb-1">Basic Plan</p>
                                    <p className="text-3xl font-bold text-gray-900">
                                        {formatCurrency(calculateMonthlyCost(basicPackage.pricePerStudentPerUnit))}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">/month</p>
                                </div>
                            )}
                            {proPackage && (
                                <div className="text-center">
                                    <p className="text-sm text-gray-600 mb-1">Pro Plan</p>
                                    <p className="text-3xl font-bold text-purple-600">
                                        {formatCurrency(calculateMonthlyCost(proPackage.pricePerStudentPerUnit))}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">/month</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Cards */}
            <div className="max-w-6xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Basic Package */}
                    {basicPackage && (
                        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{basicPackage.displayName}</h3>
                            <div className="flex items-baseline mb-4">
                                <span className="text-4xl font-bold text-gray-900">
                                    {formatCurrency(basicPackage.pricePerStudentPerUnit)}
                                </span>
                                <span className="text-gray-600 ml-2">/student/unit</span>
                            </div>
                            <p className="text-gray-600 mb-6">{basicPackage.description}</p>

                            <ul className="space-y-3 mb-8">
                                {basicPackage.features.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-700">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => alert('Institution signup coming soon!')}
                                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
                            >
                                Start Free Trial
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </button>
                        </div>
                    )}

                    {/* Pro Package */}
                    {proPackage && (
                        <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-8 text-white relative">
                            <div className="absolute top-4 right-4 bg-yellow-400 text-purple-900 px-3 py-1 rounded-full text-sm font-bold">
                                POPULAR
                            </div>

                            <h3 className="text-2xl font-bold mb-2">{proPackage.displayName}</h3>
                            <div className="flex items-baseline mb-4">
                                <span className="text-4xl font-bold">
                                    {formatCurrency(proPackage.pricePerStudentPerUnit)}
                                </span>
                                <span className="text-white/80 ml-2">/student/unit</span>
                            </div>
                            <p className="text-white/90 mb-6">{proPackage.description}</p>

                            <ul className="space-y-3 mb-8">
                                {proPackage.features.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => alert('Institution signup coming soon!')}
                                className="w-full bg-white text-purple-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
                            >
                                Start Free Trial
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white py-16">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-6">
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                How does the 30-day free trial work?
                            </h3>
                            <p className="text-gray-600">
                                When you sign up, you get full access to all features of your chosen plan for 30 days at no cost. No credit card required. After the trial, you'll be billed monthly based on your actual usage.
                            </p>
                        </div>

                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                How is the monthly bill calculated?
                            </h3>
                            <p className="text-gray-600">
                                Your monthly bill is calculated as: (Number of enrolled students) × (Number of units taken) × (Price per student per unit). You only pay for active enrollments.
                            </p>
                        </div>

                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Can I upgrade or downgrade my plan?
                            </h3>
                            <p className="text-gray-600">
                                Yes! You can change your plan at any time from your admin dashboard. Changes take effect immediately, and billing is prorated.
                            </p>
                        </div>

                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                What payment methods do you accept?
                            </h3>
                            <p className="text-gray-600">
                                We accept M-PESA, bank transfers, and major credit/debit cards. All payments are processed securely.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Ready to Get Started?
                    </h2>
                    <p className="text-xl text-white/90 mb-8">
                        Join hundreds of institutions transforming education with our platform
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
                    >
                        Back to Home
                        <ChevronRight className="w-5 h-5 ml-2" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Pricing;
